import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';

import AdvertiserPagesService, { AdvertiserPage, AdvertiserPageGroup } from 'services/AdvertiserPagesService';

import AdvertiserPagesDialog from './AdvertiserPagesDialog';

// Picks which advertisers an Ad Library fetch is limited to. Used by BOTH Video
// Creative and Static Creative — the list is shared, because an advertiser worth
// watching is worth watching in either format.
//
// Groups and individual pages are separate selections rather than one merged
// list. A group is a LIVE reference: targeting "Mellow Pages" means whatever is
// in Mellow when the scrape runs, so adding an eighth Mellow page widens every
// keyword already targeting it. Flattening groups into page ids at selection
// time would freeze that and quietly diverge from the group.
//
// Selecting nothing means every advertiser, which is how every fetch behaved
// before this existed — stated in the UI rather than implied, because "no
// filter" and "filter that matched nothing" look identical in a result count.

interface AdvertiserPagePickerProps {
  pageIds: string[];
  pageGroupIds: string[];
  onChange: (next: { pageIds: string[]; pageGroupIds: string[] }) => void;
  disabled?: boolean;
}

export default function AdvertiserPagePicker({
  pageIds, pageGroupIds, onChange, disabled,
}: AdvertiserPagePickerProps) {
  const service = useMemo(() => new AdvertiserPagesService(), []);

  const [pages, setPages] = useState<AdvertiserPage[]>([]);
  const [groups, setGroups] = useState<AdvertiserPageGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [managing, setManaging] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [pageResp, groupResp] = await Promise.all([service.list(true), service.listGroups(true)]);
    if (pageResp) setPages(pageResp.pages);
    if (groupResp) setGroups(groupResp.groups);
    setLoading(false);
  }, [service]);

  useEffect(() => { void load(); }, [load]);

  const selectedGroups = groups.filter(g => pageGroupIds.includes(g.groupId));
  const selectedPages = pages.filter(p => pageIds.includes(p.pageId));

  // What the fetch will actually target, de-duplicated the same way the backend
  // does — so the count here matches the count that ends up in the search URL.
  const effective = new Set<string>([
    ...pageIds,
    ...selectedGroups.flatMap(g => g.pageIds),
  ]);

  return (
    <Box>
      <Stack spacing={1.5}>
        <Autocomplete
          multiple
          disabled={disabled || loading}
          options={groups}
          value={selectedGroups}
          getOptionLabel={g => g.groupName}
          isOptionEqualToValue={(a, b) => a.groupId === b.groupId}
          onChange={(_e, next) => onChange({ pageIds, pageGroupIds: next.map(g => g.groupId) })}
          renderOption={(props, g) => (
            <li {...props} key={g.groupId}>
              <Box>
                <Typography variant="body2">{g.groupName}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {g.pageIds.length} page{g.pageIds.length === 1 ? '' : 's'}
                </Typography>
              </Box>
            </li>
          )}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((g, index) => (
              <Chip
                size="small"
                color="primary"
                label={`${g.groupName} (${g.pageIds.length})`}
                {...getTagProps({ index })}
                key={g.groupId}
              />
            ))
          }
          renderInput={params => (
            <TextField {...params} size="small" label="Advertiser groups (optional)" placeholder={selectedGroups.length ? '' : 'None'} />
          )}
        />

        <Autocomplete
          multiple
          disabled={disabled || loading}
          options={pages}
          value={selectedPages}
          getOptionLabel={p => p.pageName}
          isOptionEqualToValue={(a, b) => a.pageId === b.pageId}
          onChange={(_e, next) => onChange({ pageIds: next.map(p => p.pageId), pageGroupIds })}
          renderOption={(props, p) => (
            <li {...props} key={p.pageId}>
              <Box>
                <Typography variant="body2">{p.pageName}</Typography>
                <Typography variant="caption" color="text.secondary">{p.pageId}</Typography>
              </Box>
            </li>
          )}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((p, index) => (
              <Chip size="small" label={p.pageName} {...getTagProps({ index })} key={p.pageId} />
            ))
          }
          renderInput={params => (
            <TextField {...params} size="small" label="Individual pages (optional)" placeholder={selectedPages.length ? '' : 'None'} />
          )}
        />
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          {effective.size
            ? `Fetches ads from ${effective.size} advertiser page${effective.size === 1 ? '' : 's'}.`
            : 'No filter — fetches ads from every advertiser.'}
        </Typography>
        <Button size="small" startIcon={<SettingsOutlinedIcon />} disabled={disabled} onClick={() => setManaging(true)}>
          Manage pages
        </Button>
      </Stack>

      <AdvertiserPagesDialog open={managing} onClose={() => setManaging(false)} onChanged={load} />
    </Box>
  );
}
